import { useEffect, useState } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface StickyEnquiryBarProps {
  title: string;
  eyebrow?: string;
  onEnquire: () => void;
}

/**
 * Mobile-only action bar that keeps the enquiry pop-up one tap away while the
 * visitor scrolls through a detail page. It slides in after the hero and hides
 * again near the page bottom so it never covers the closing calls to action.
 */
export default function StickyEnquiryBar({ title, eyebrow, onEnquire }: StickyEnquiryBarProps) {
  const [visible, setVisible] = useState(false);
  const { brand } = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200;
      setVisible(window.scrollY > 560 && !nearBottom);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <div
      className={`lg:hidden fixed inset-x-0 bottom-0 z-50 transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-primary-950/95 backdrop-blur-md border-t border-background-50/10 px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-300">
            {eyebrow || "Enquire"}
          </p>
          <p className="text-sm font-medium text-background-50 truncate">{title}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`tel:${brand.phone}`}
            aria-label={`Call us on ${brand.phone}`}
            className="inline-flex items-center gap-1.5 bg-background-50/10 border border-background-50/20 text-background-50 text-sm font-semibold px-3.5 py-3 rounded-md hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill text-base"></i>
            Call us
          </a>
          <button
            type="button"
            onClick={onEnquire}
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-3 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            Enquire
          </button>
        </div>
      </div>
    </div>
  );
}